import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useHostGlobals from 'appHost/Globals';
import { AxiosError } from 'axios';
import { ErrorViewer, FormActions, IncompleteTruckerProfile, IncompleteTruckerProfileAlert } from '@/components';
import { useAppContext } from '@/context';
import { Trucker, Compositions, Travel } from './Steps';
import { Form, FormSeparator, Callout, Footer, Wrapper } from './styled';
import { useValidate } from './validation';
import { validate, object, boolean, string } from '@/utils/validations';
import { useErrorHandler, useFreight, useGlobal, useToast, useTracker } from '@/hooks';
import { ForbiddenError, createSearchService, updateSearchService, sendWhatsAppService } from '@/services';
import { MissingTruckerProfileType } from '@/components/IncompleteTruckerProfile/types';
import { FREIGHT_LISTING, RISK_LIST_SUCCESS, RISK_ONBOARDING_URL } from '@/constants';
import { getPartnerLabel, useFieldFocus, useSetFormValue } from '@/utils';
import { parseFormToRiskModel } from '@/services/utils';
import { DynamicObject } from './Steps/Compositions/types';
import { Partner } from './Steps/Partner';

const steps = [
  (): JSX.Element => <Trucker />,
  (): JSX.Element => <Compositions />,
  (): JSX.Element => <Travel />,
  (): JSX.Element => <Partner />,
];

export const FormWrapper: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const tracker = useTracker();
  const global = useHostGlobals();
  const { user } = useGlobal();
  const [isOpenIncompleteTruckerProfileAlert, setIsOpenIncompleteTruckerProfileAlert] = useState(false);
  const [isOpenIncompleteProfile, setIsOpenIncompleteProfile] = useState(false);
  const [missingTruckerProfileType, setMissingTruckerProfileType] = useState<MissingTruckerProfileType>(null);
  const [needRetriveGlobalState, setNeedRetriveGlobalState] = useState(false);
  const navigate = useNavigate();
  const schemas = useValidate();
  const toast = useToast();
  const { state, dispatch } = useAppContext();
  const focusFieldWithError = useFieldFocus();
  const { freightId, truckerId, navigateWithFreight, verifyIfHasFarePaid } = useFreight();
  const [hasFarePaid, setFarePaid] = useState(false);
  const setFormValue = useSetFormValue(dispatch);
  const formSteps = useMemo(() => state.formSteps, [state.formSteps]);
  const risk = useMemo(() => state.risk, [state.risk]);
  const isResubmit = useMemo(() => Boolean(risk.backendResponse?.uuid), [risk.backendResponse]);
  const truckerProfile = useMemo(
    () => state.truckerProfile,
    [state.truckerProfile.isLoaded, state.truckerProfile.isAllDone],
  );

  const createSearchServiceErrorHandler = useErrorHandler((error) => {
    if (error instanceof ForbiddenError) {
      navigate(RISK_ONBOARDING_URL, { replace: true });
      return;
    }

    const axiosError = error as AxiosError;
    dispatch({ action: 'set_submit_load', payload: false });
    toast.add({ title: error.message, type: 'error' });
    tracker.dispatchWithUserData('risk_management_form_submit_failed', {
      error: error.message,
      httpStatusCode: axiosError.status,
      stack: error.stack,
      data: JSON.stringify(risk.data),
    });
  });

  const onCloseIncompleteTruckerProfileAlert = () => {
    setIsOpenIncompleteTruckerProfileAlert(false);
    if (Boolean(freightId)) navigateWithFreight(FREIGHT_LISTING);
    else navigate(RISK_LIST_SUCCESS);
  };

  const setStep = () => {
    const { current } = formSteps;
    const realStepIndex = (current >= steps.length ? steps.length : current) - 1;
    setCurrentStep(realStepIndex);
  };

  const handleValidation = () => {
    const errors = validate(schemas(formSteps.current), risk.data);
    const isValid = formSteps.current >= formSteps.steps ? false : errors === undefined;

    if (!isValid) tracker.dispatchWithUserData('risk_management_form_validation_failed', { errors });

    dispatch({ action: 'risk_set_is_valid', payload: isValid });

    return isValid;
  };

  const submitForm = (): void => {
    dispatch({ action: 'set_submit_load', payload: true });

    const onSuccess = () => {
      dispatch({ action: 'form_step_next' });

      if (missingTruckerProfileType) {
        setIsOpenIncompleteTruckerProfileAlert(true);
        return;
      }

      tracker.dispatchWithUserData('risk_management_form_submit_confirmed', {
        parter: getPartnerLabel(state.risk.data.partnerId),
      });
      const partnerLabel = getPartnerLabel(state.risk.data.partnerId);
      toast.add({ title: `Pesquisa enviada com sucesso para a ${partnerLabel}.`, type: 'success' });

      setTimeout(() => {
        dispatch({ action: 'risk_form_reset' });
        if (Boolean(freightId)) navigateWithFreight(FREIGHT_LISTING);
        else navigate(RISK_LIST_SUCCESS);
      }, 2000);
    };

    try {
      const nullableFreightId = hasFarePaid && freightId ? freightId : null;
      const parsedFormRiskModel = parseFormToRiskModel(risk.data, nullableFreightId);
      const formId = state.formId.value;

      if (Boolean(state.risk.backendResponse?.uuid)) {
        updateSearchService(parsedFormRiskModel, formId).then(onSuccess).catch(createSearchServiceErrorHandler);
      } else {
        createSearchService(parsedFormRiskModel, formId).then(onSuccess).catch(createSearchServiceErrorHandler);
      }
    } catch (error) {
      dispatch({ action: 'set_submit_load', payload: false });
      toast.add({ title: 'Ocorreu algum erro. Por favor, tente novamente mais tarde.', type: 'error' });
      tracker.dispatchWithUserData('risk_management_form_submit_failed', {
        error: 'Failed to parse form data to RiskModel',
        stack: error.stack,
        data: JSON.stringify(risk.data),
        originalError: error?.originalError,
      });
    }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const isValid = handleValidation();

    tracker.dispatchWithUserData('risk_management_form_submit_clicked');

    if (!risk.triedToSubmit) {
      dispatch({ action: 'risk_set_tried_to_submit', payload: true });
    }

    if (!isValid) {
      const errors = validate(schemas(formSteps.current), risk.data);
      focusFieldWithError(errors as DynamicObject);
      return;
    }

    if (isValid) {
      dispatch({ action: 'risk_set_tried_to_submit', payload: false });
      dispatch({ action: 'risk_set_is_valid', payload: false });

      if (formSteps.current < formSteps.steps - 1) {
        dispatch({ action: 'form_step_next' });
        return;
      }
    }

    submitForm();
  };

  const handleTruckerInfoType = () => {
    const {
      isLoaded,
      isAllDone,
      data: { isRegistered, isSelfValid, truckerIsActive },
    } = truckerProfile;

    if (!isLoaded || !isAllDone || (isLoaded && isRegistered && isSelfValid)) {
      setIsOpenIncompleteProfile(false);
      setMissingTruckerProfileType(null);
      return;
    }

    if (isRegistered === undefined || isSelfValid === undefined || (isRegistered && !truckerIsActive)) {
      return;
    }

    if (isRegistered && !isSelfValid) {
      setMissingTruckerProfileType('noSelfIsValid');
    } else if (!isRegistered) {
      setMissingTruckerProfileType('noRegistered');
    }

    setIsOpenIncompleteProfile(true);
  };

  const schema = object({
    cpf: string().required(),
    birthday: string().required(),
    cellPhone: string().required(),
    fatherName: string().nullable(),
    isRegistered: boolean().required(),
    isSelfValid: boolean().required(),
    motherName: string().required(),
    name: string().required(),
    profileId: string().nullable(),
    riskId: string().nullable(),
    type: string().required(),
    address: object({
      complement: string().nullable(),
      neighborhood: string().nullable(),
      number: string().nullable(),
      street: string().required(),
      zipCode: string().required(),
      location: object({ place: string().required(), name: string().required(), stateAbbr: string().required() }),
    }),
    cnh: object({
      category: string().required(),
      dateFirstLicense: string().required(),
      expirationDate: string().required(),
      registrationNumber: string().required(),
      securityNumber: string().required(),
      stateEmission: string().required(),
    }),
  });

  const handleRetrieveGlobalState = () => {
    if (!risk.data.trucker.cellPhone || !risk.data.trucker.cpf || !needRetriveGlobalState) return;
    document.getElementById('search-button').click();
    setNeedRetriveGlobalState(false);
  };

  const retrieveGlobalState = () => {
    if (Boolean(truckerId)) return;
    const hasValidTruckerProfileData = schema.isValidSync(global?.truckerProfile);

    if (hasValidTruckerProfileData) {
      dispatch({ action: 'risk_update_trucker', payload: global?.truckerProfile });
      dispatch({ action: 'trucker_profile_update_data', payload: global?.truckerProfile });
      dispatch({ action: 'form_step_next' });
      dispatch({ action: 'trucker_profile_set_all_done', payload: true });
    } else if (global?.truckerProfile?.cpf && global?.truckerProfile?.cellPhone) {
      setFormValue('trucker.cpf', global?.truckerProfile?.cpf);
      setFormValue('trucker.cellPhone', global?.truckerProfile?.cellPhone);
      dispatch({ action: 'trucker_profile_set_all_done', payload: false });
      setNeedRetriveGlobalState(true);
    }
  };

  const handleFirstStepInitialize = () => {
    if (!truckerProfile.isCpfAutoLoaded || !handleValidation()) return;
    if (!truckerProfile.data.isDriverPhone) return;

    dispatch({ action: 'form_step_next' });
    dispatch({ action: 'trucker_profile_all_auto_loaded', payload: true });
    dispatch({ action: 'trucker_profile_set_all_done', payload: true });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
  };

  const whatsAppEvent = () => {
    if (missingTruckerProfileType === 'noRegistered') {
      sendWhatsAppService(risk.data.trucker.cellPhone, user.data.company.name);
    }
  };

  const consultFarePaid = () => {
    if (isResubmit) return;
    verifyIfHasFarePaid().then((data) => setFarePaid(data));
  };

  useEffect(setStep, [formSteps.current]);
  useEffect(handleTruckerInfoType, [truckerProfile]);
  useEffect(scrollToTop, [currentStep]);
  useEffect(handleRetrieveGlobalState, [risk.data.trucker.cellPhone, risk.data.trucker.cpf, needRetriveGlobalState]);
  useEffect(handleFirstStepInitialize, []);
  useEffect(consultFarePaid, [isResubmit, truckerProfile.data.profileId]);
  useEffect(retrieveGlobalState, []);
  useEffect(whatsAppEvent, [missingTruckerProfileType, risk.data.trucker.cellPhone]);

  const CurrentPage = useMemo(() => steps[currentStep], [currentStep]);

  return (
    <Wrapper>
      {!!state.risk.backendResponse?.uuid && (
        <Callout
          message='Alguns dados foram ocultos com "*" para atender a LGPD.'
          color="blue"
          icon="information"
          fullWidth
          iconShape
        />
      )}
      <Form onSubmit={onSubmit}>
        <CurrentPage />
        <Footer>
          <FormSeparator />
          <FormActions />
        </Footer>
      </Form>

      <IncompleteTruckerProfile
        type={missingTruckerProfileType}
        cellPhone={risk.data.trucker.cellPhone}
        open={isOpenIncompleteProfile}
      />
      <IncompleteTruckerProfileAlert
        type={missingTruckerProfileType}
        cellPhone={risk.data.trucker.cellPhone}
        open={isOpenIncompleteTruckerProfileAlert}
        onClose={onCloseIncompleteTruckerProfileAlert}
      />
      <ErrorViewer />
    </Wrapper>
  );
};
